import { useId } from 'react';

import { Card, CardHeader, ContentLoader } from '@/components/common';
import { useCoverPreference, useSetCoverPreference } from '@/hooks/usePospHub';
import { getErrorMessage } from '@/services';
import type { CoverPreference } from '@/services/pospHub';

const HINTS: Record<CoverPreference, string> = {
  comprehensive: 'Your quotes for customers price comprehensive cover. You can still add third-party only on any quote.',
  tp_only: 'Your quotes price third-party only. A brand-new vehicle is always priced comprehensive.',
  both: 'Your quotes price every cover the vehicle can have.',
};

/**
 * Messaging Plan 2 — which covers the agent's quotes for customers price in the
 * Telegram bot (and, later, through MCP). The same setting as Telegram's
 * ⚙️ Settings, over the same endpoint.
 *
 * Four honest states: loading, a failed fetch (with a retry — never an empty
 * card), an answer we cannot read (shown as unavailable), and the form. The
 * checked radio is always the server's answer.
 */
export function PospCoverPreferenceCard() {
  const { data, isLoading, isError, refetch } = useCoverPreference();
  const save = useSetCoverPreference();
  const idBase = useId();
  const readable = !!data && Array.isArray(data.choices) && typeof data.cover_preference === 'string';

  return (
    <Card>
      <CardHeader
        title="Quote preferences"
        subtitle="Which covers are priced when you quote for a customer in the Telegram bot"
      />
      {isLoading && <ContentLoader />}
      {isError && (
        <div role="alert" className="text-sm text-red-700 dark:text-red-300">
          Could not load your quote preferences.{' '}
          <button
            type="button"
            onClick={() => refetch()}
            className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            Try again
          </button>
        </div>
      )}
      {!isLoading && !isError && !readable && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Your quote preferences are unavailable right now.</p>
      )}
      {readable && (
        <fieldset disabled={save.isPending} className="space-y-3">
          <legend className="sr-only">Covers to price</legend>
          {data.choices.map((choice) => {
            // The hint is a DESCRIPTION, not part of the radio's name: inside
            // the label it made "Comprehensive only" answer to "third-party
            // only" too (its hint mentions adding third-party only).
            const inputId = `${idBase}-${choice.value}`;
            const hint = HINTS[choice.value];
            return (
              <div key={choice.value} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                <input
                  id={inputId}
                  type="radio"
                  name="posp-cover-preference"
                  value={choice.value}
                  checked={data.cover_preference === choice.value}
                  onChange={() => save.mutate(choice.value)}
                  aria-describedby={hint ? `${inputId}-hint` : undefined}
                  className="mt-0.5 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <label htmlFor={inputId}>{choice.label}</label>
                  {hint && (
                    <p id={`${inputId}-hint`} className="text-xs text-gray-500 dark:text-gray-400">
                      {hint}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {save.isError && (
            <p role="alert" className="text-sm text-red-700 dark:text-red-300">
              {getErrorMessage(save.error)}
            </p>
          )}
          {save.isSuccess && (
            <p role="status" className="text-sm text-green-700 dark:text-green-300">
              Saved. New quotes use this.
            </p>
          )}
        </fieldset>
      )}
    </Card>
  );
}
